import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";

const API_BASE =
  "https://crosslisting-backend-aacranbzdyd3ffbd.centralindia-01.azurewebsites.net/api/listing-agent";

// ─────────────────────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────────────────────
const fmtPrice = (n, cur = "GBP") => {
  if (n == null) return "—";
  return `${cur === "EUR" ? "€" : cur === "USD" ? "$" : "£"}${Number(n).toFixed(2)}`;
};
const fmt = (n) => (n == null ? "—" : Number(n).toLocaleString("en-GB"));
const pct = (n) => (n == null ? "—" : `${n}%`);

const PRIORITY_CFG = {
  high: {
    color: "#991b1b",
    bg: "#fee2e2",
    border: "#fca5a5",
    label: "High Priority",
  },
  medium: {
    color: "#92400e",
    bg: "#fef3c7",
    border: "#fde68a",
    label: "Medium",
  },
  low: { color: "#1e40af", bg: "#dbeafe", border: "#93c5fd", label: "Low" },
};

const AREA_ICON = {
  title: "📝",
  price: "💰",
  image_or_price: "🖼",
  description_or_price: "📄",
  seo: "🔍",
};

// ─────────────────────────────────────────────────────────────────────────────
// SKELETON
// ─────────────────────────────────────────────────────────────────────────────
const Sk = ({ w = "100%", h = 14, r = 6, style = {} }) => (
  <div
    style={{
      width: w,
      height: h,
      borderRadius: r,
      background: "linear-gradient(90deg,#f0f0f0 25%,#e8e8e8 50%,#f0f0f0 75%)",
      backgroundSize: "200% 100%",
      animation: "shimmer 1.3s infinite",
      ...style,
    }}
  />
);
const SkRow = () => (
  <tr style={{ borderBottom: "1px solid #f3f4f6" }}>
    <td style={{ padding: "14px 16px" }}>
      <Sk w={240} h={13} style={{ marginBottom: 6 }} />
      <Sk w={80} h={10} />
    </td>
    {[60, 60, 60, 60, 80, 130, 110].map((w, i) => (
      <td key={i} style={{ padding: "14px 16px" }}>
        <Sk w={w} h={16} />
      </td>
    ))}
  </tr>
);

// ─────────────────────────────────────────────────────────────────────────────
// MINI BAR CHART — price bands
// ─────────────────────────────────────────────────────────────────────────────
const PriceBandChart = ({ bands, ourPrice }) => {
  const labels = {
    under10: "< £10",
    ten_20: "£10–20",
    twenty_30: "£20–30",
    over30: "> £30",
  };
  const max = Math.max(...Object.values(bands), 1);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {Object.entries(bands).map(([key, count]) => {
        const label = labels[key];
        const inRange =
          (key === "under10" && ourPrice < 10) ||
          (key === "ten_20" && ourPrice >= 10 && ourPrice < 20) ||
          (key === "twenty_30" && ourPrice >= 20 && ourPrice < 30) ||
          (key === "over30" && ourPrice >= 30);
        return (
          <div
            key={key}
            style={{ display: "flex", alignItems: "center", gap: 8 }}
          >
            <div
              style={{
                width: 60,
                fontSize: 10,
                color: inRange ? "#1d4ed8" : "#6b7280",
                fontWeight: inRange ? 800 : 500,
                flexShrink: 0,
              }}
            >
              {label} {inRange && "← you"}
            </div>
            <div
              style={{
                flex: 1,
                background: "#f3f4f6",
                borderRadius: 99,
                height: 8,
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  height: "100%",
                  borderRadius: 99,
                  width: `${(count / max) * 100}%`,
                  background: inRange ? "#1d4ed8" : "#d1d5db",
                  transition: "width 0.6s ease",
                }}
              />
            </div>
            <div
              style={{
                width: 20,
                fontSize: 11,
                fontWeight: 700,
                color: "#374151",
                textAlign: "right",
              }}
            >
              {count}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// FUNNEL DISPLAY
// ─────────────────────────────────────────────────────────────────────────────
const Funnel = ({ impressions, views, sold, viewRate, sellRate }) => {
  const steps = [
    {
      label: "Impressions",
      val: fmt(impressions),
      sub: "shown in search",
      pctVal: null,
      color: "#6d28d9",
    },
    {
      label: "Views",
      val: fmt(views),
      sub: `${pct(viewRate)} click-through`,
      pctVal: viewRate,
      color: "#1d4ed8",
    },
    {
      label: "Sales",
      val: fmt(sold),
      sub: `${pct(sellRate)} conversion`,
      pctVal: sellRate,
      color: "#065f46",
    },
  ];
  return (
    <div style={{ display: "flex", gap: 8, alignItems: "stretch" }}>
      {steps.map((s, i) => (
        <React.Fragment key={s.label}>
          <div
            style={{
              flex: 1,
              background: "#f9fafb",
              border: "1px solid #e5e7eb",
              borderRadius: 10,
              padding: "12px 14px",
              textAlign: "center",
            }}
          >
            <div style={{ fontSize: 22, fontWeight: 800, color: s.color }}>
              {s.val}
            </div>
            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: "#374151",
                marginTop: 2,
              }}
            >
              {s.label}
            </div>
            <div style={{ fontSize: 10, color: "#9ca3af", marginTop: 2 }}>
              {s.sub}
            </div>
          </div>
          {i < steps.length - 1 && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                color: "#d1d5db",
                fontSize: 18,
              }}
            >
              →
            </div>
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// ACTION CARD
// ─────────────────────────────────────────────────────────────────────────────
const ActionCard = ({ action, index }) => {
  const cfg = PRIORITY_CFG[action.priority] || PRIORITY_CFG.low;
  return (
    <div
      style={{
        background: "#fff",
        border: `1px solid ${cfg.border}`,
        borderLeft: `4px solid ${cfg.color}`,
        borderRadius: 10,
        padding: "14px 16px",
        animation: `fadeIn 0.25s ease ${index * 0.08}s both`,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginBottom: 8,
        }}
      >
        <span style={{ fontSize: 16 }}>{AREA_ICON[action.area] || "⚡"}</span>
        <span
          style={{
            fontSize: 10,
            fontWeight: 800,
            color: cfg.color,
            background: cfg.bg,
            border: `1px solid ${cfg.border}`,
            borderRadius: 20,
            padding: "2px 8px",
            textTransform: "uppercase",
            letterSpacing: 0.5,
          }}
        >
          {cfg.label}
        </span>
      </div>
      <div
        style={{
          fontSize: 13,
          fontWeight: 600,
          color: "#111827",
          marginBottom: 6,
          lineHeight: 1.4,
        }}
      >
        {action.issue}
      </div>
      <div
        style={{
          fontSize: 12,
          color: "#374151",
          lineHeight: 1.5,
          background: "#f9fafb",
          borderRadius: 6,
          padding: "8px 10px",
          borderLeft: "2px solid #e5e7eb",
        }}
      >
        ✏️ <strong>Fix:</strong> {action.fix}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// KEYWORD CHIP
// ─────────────────────────────────────────────────────────────────────────────
const KeywordChip = ({ word, count, pctVal, missing }) => (
  <div
    style={{
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      background: missing ? "#fee2e2" : "#f0fdf4",
      border: `1px solid ${missing ? "#fca5a5" : "#86efac"}`,
      borderRadius: 20,
      padding: "4px 10px",
      margin: "3px",
    }}
  >
    <span
      style={{
        fontSize: 12,
        fontWeight: 700,
        color: missing ? "#991b1b" : "#065f46",
      }}
    >
      {word}
    </span>
    <span
      style={{
        fontSize: 10,
        color: missing ? "#b91c1c" : "#16a34a",
        fontWeight: 600,
      }}
    >
      {pctVal}% of competitors
    </span>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// DETAIL PAGE — full analysis
// ─────────────────────────────────────────────────────────────────────────────
const ListingDetailPage = ({ ebayItemId, onBack }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    axios
      .get(`${API_BASE}/analysis/${ebayItemId}`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e.response?.data?.message || e.message))
      .finally(() => setLoading(false));
  }, [ebayItemId]);

  const Section = ({ title, children, icon }) => (
    <div
      style={{
        background: "#fff",
        border: "1px solid #e5e7eb",
        borderRadius: 14,
        overflow: "hidden",
        marginBottom: 20,
        boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
      }}
    >
      <div
        style={{
          padding: "14px 20px",
          borderBottom: "1px solid #f3f4f6",
          display: "flex",
          alignItems: "center",
          gap: 8,
          background: "#fafafa",
        }}
      >
        <span style={{ fontSize: 16 }}>{icon}</span>
        <span style={{ fontSize: 13, fontWeight: 800, color: "#111827" }}>
          {title}
        </span>
      </div>
      <div style={{ padding: "18px 20px" }}>{children}</div>
    </div>
  );

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#f9fafb",
        fontFamily: "-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif",
      }}
    >
      <style>{`
        @keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
        @keyframes fadeIn  { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }
        @keyframes spin    { to{transform:rotate(360deg)} }
      `}</style>

      {/* Top nav */}
      <div
        style={{
          background: "#fff",
          borderBottom: "1px solid #e5e7eb",
          padding: "14px 24px",
          display: "flex",
          alignItems: "center",
          gap: 14,
          position: "sticky",
          top: 0,
          zIndex: 10,
        }}
      >
        <button
          onClick={onBack}
          style={{
            background: "#f3f4f6",
            border: "none",
            borderRadius: 8,
            padding: "7px 14px",
            fontSize: 13,
            fontWeight: 700,
            color: "#374151",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          ← Back
        </button>
        <div>
          <div style={{ fontSize: 14, fontWeight: 800, color: "#111827" }}>
            Listing Analysis
          </div>
          <div
            style={{ fontSize: 10, color: "#9ca3af", fontFamily: "monospace" }}
          >
            #{ebayItemId}
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 900, margin: "0 auto", padding: "28px 24px" }}>
        {loading && (
          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            {[...Array(5)].map((_, i) => (
              <div
                key={i}
                style={{
                  background: "#fff",
                  borderRadius: 14,
                  padding: 20,
                  border: "1px solid #e5e7eb",
                }}
              >
                <Sk w="40%" h={14} style={{ marginBottom: 16 }} />
                <Sk w="100%" h={10} style={{ marginBottom: 8 }} />
                <Sk w="80%" h={10} style={{ marginBottom: 8 }} />
                <Sk w="60%" h={10} />
              </div>
            ))}
          </div>
        )}

        {error && (
          <div
            style={{
              background: "#fee2e2",
              border: "1px solid #fca5a5",
              borderRadius: 12,
              padding: 20,
              color: "#991b1b",
            }}
          >
            ⚠️ {error}
          </div>
        )}

        {data && !loading && (
          <>
            {/* ── Listing header ── */}
            <div
              style={{
                background: "#fff",
                border: "1px solid #e5e7eb",
                borderRadius: 14,
                padding: "20px 24px",
                marginBottom: 20,
                animation: "fadeIn 0.3s ease",
              }}
            >
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: "#9ca3af",
                  textTransform: "uppercase",
                  letterSpacing: 1,
                  marginBottom: 8,
                }}
              >
                {data.our_listing?.date_range || "Latest Period"}
              </div>
              <a
                href={data.our_listing?.item_url}
                target="_blank"
                rel="noreferrer"
                style={{
                  fontSize: 18,
                  fontWeight: 800,
                  color: "#1d4ed8",
                  textDecoration: "none",
                  lineHeight: 1.4,
                  display: "block",
                  marginBottom: 12,
                }}
              >
                {data.our_listing?.title || "—"}
              </a>
              <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>
                <div>
                  <span
                    style={{ fontSize: 11, color: "#9ca3af", fontWeight: 600 }}
                  >
                    Price
                  </span>
                  <br />
                  <span
                    style={{ fontSize: 20, fontWeight: 800, color: "#111827" }}
                  >
                    {fmtPrice(data.our_listing?.price)}
                  </span>
                </div>
                <div>
                  <span
                    style={{ fontSize: 11, color: "#9ca3af", fontWeight: 600 }}
                  >
                    Condition
                  </span>
                  <br />
                  <span
                    style={{ fontSize: 14, fontWeight: 700, color: "#374151" }}
                  >
                    {data.our_listing?.ebay_condition || "—"}
                  </span>
                </div>
                <div>
                  <span
                    style={{ fontSize: 11, color: "#9ca3af", fontWeight: 600 }}
                  >
                    SKU
                  </span>
                  <br />
                  <span
                    style={{
                      fontSize: 12,
                      fontFamily: "monospace",
                      color: "#6b7280",
                    }}
                  >
                    {data.our_listing?.sku || "—"}
                  </span>
                </div>
              </div>
            </div>

            {/* ── Action items — most important first ── */}
            {data.actions?.length > 0 && (
              <Section title={`${data.actions.length} Issues to Fix`} icon="⚡">
                <div
                  style={{ display: "flex", flexDirection: "column", gap: 10 }}
                >
                  {data.actions.map((a, i) => (
                    <ActionCard key={i} action={a} index={i} />
                  ))}
                </div>
              </Section>
            )}

            {/* ── Performance funnel ── */}
            <Section title="Performance Funnel" icon="📊">
              <Funnel
                impressions={data.our_listing.impressions}
                views={data.our_listing.views}
                sold={data.our_listing.sold}
                viewRate={data.our_listing.view_rate}
                sellRate={data.our_listing.sell_rate}
              />
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(3,1fr)",
                  gap: 10,
                  marginTop: 14,
                }}
              >
                {[
                  [
                    "External Views",
                    fmt(data.our_listing?.externalViews),
                    "#374151",
                  ],
                  [
                    "Available",
                    fmt(data.our_listing?.available_items),
                    "#374151",
                  ],
                  [
                    "CTR",
                    pct(data.our_listing?.ctr),
                    data.our_listing?.ctr < 2 ? "#991b1b" : "#065f46",
                  ],
                ].map(([k, v, col]) => (
                  <div
                    key={k}
                    style={{
                      background: "#f9fafb",
                      borderRadius: 8,
                      padding: "10px 14px",
                    }}
                  >
                    <div
                      style={{
                        fontSize: 10,
                        color: "#9ca3af",
                        fontWeight: 700,
                        textTransform: "uppercase",
                        marginBottom: 4,
                      }}
                    >
                      {k}
                    </div>
                    <div style={{ fontSize: 18, fontWeight: 800, color: col }}>
                      {v}
                    </div>
                  </div>
                ))}
              </div>
            </Section>

            {/* ── Price analysis ── */}
            <Section title="Price Analysis" icon="💰">
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 20,
                }}
              >
                {/* Left: numbers */}
                <div>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 10,
                      marginBottom: 16,
                    }}
                  >
                    {[
                      [
                        "Our Price",
                        fmtPrice(data.price_analysis.our_price),
                        "#1d4ed8",
                        true,
                      ],
                      [
                        "Competitor Avg",
                        fmtPrice(data.price_analysis.avg_price),
                        "#374151",
                        false,
                      ],
                      [
                        "Median",
                        fmtPrice(data.price_analysis.median_price),
                        "#374151",
                        false,
                      ],
                      [
                        "Lowest",
                        fmtPrice(data.price_analysis.min_price),
                        "#065f46",
                        false,
                      ],
                      [
                        "Highest",
                        fmtPrice(data.price_analysis.max_price),
                        "#991b1b",
                        false,
                      ],
                    ].map(([k, v, col, bold]) => (
                      <div
                        key={k}
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          padding: "8px 0",
                          borderBottom: "1px solid #f3f4f6",
                        }}
                      >
                        <span
                          style={{
                            fontSize: 12,
                            color: "#6b7280",
                            fontWeight: bold ? 700 : 500,
                          }}
                        >
                          {k}
                        </span>
                        <span
                          style={{
                            fontSize: bold ? 18 : 15,
                            fontWeight: 800,
                            color: col,
                          }}
                        >
                          {v}
                        </span>
                      </div>
                    ))}
                  </div>
                  {data.price_analysis.price_diff != null && (
                    <div
                      style={{
                        padding: "10px 14px",
                        borderRadius: 8,
                        background:
                          data.price_analysis.price_status === "too_high"
                            ? "#fee2e2"
                            : data.price_analysis.price_status === "too_low"
                              ? "#d1fae5"
                              : "#dbeafe",
                        fontSize: 12,
                        fontWeight: 600,
                        color:
                          data.price_analysis.price_status === "too_high"
                            ? "#991b1b"
                            : data.price_analysis.price_status === "too_low"
                              ? "#065f46"
                              : "#1e40af",
                      }}
                    >
                      {data.price_analysis.price_diff > 0
                        ? `You are £${data.price_analysis.price_diff.toFixed(2)} ABOVE competitor average`
                        : data.price_analysis.price_diff < 0
                          ? `You are £${Math.abs(data.price_analysis.price_diff).toFixed(2)} BELOW competitor average`
                          : "Your price matches competitor average"}
                    </div>
                  )}
                </div>

                {/* Right: price band chart */}
                <div>
                  <div
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      color: "#6b7280",
                      textTransform: "uppercase",
                      marginBottom: 12,
                    }}
                  >
                    Where competitors are priced
                  </div>
                  <PriceBandChart
                    bands={data.price_analysis.price_bands}
                    ourPrice={data.price_analysis.our_price}
                  />
                  <div
                    style={{ fontSize: 11, color: "#9ca3af", marginTop: 10 }}
                  >
                    Based on {data.price_analysis.total_with_price} competitors
                    with prices
                  </div>
                </div>
              </div>
            </Section>

            {/* ── Title analysis ── */}
            <Section title="Title Analysis" icon="📝">
              {/* Our title highlighted */}
              <div
                style={{
                  background: "#f0f9ff",
                  border: "1px solid #bae6fd",
                  borderRadius: 8,
                  padding: 14,
                  marginBottom: 16,
                }}
              >
                <div
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    color: "#0369a1",
                    marginBottom: 6,
                    textTransform: "uppercase",
                  }}
                >
                  Your Title ({data.title_analysis.our_title_length} chars)
                </div>
                <div
                  style={{
                    fontSize: 14,
                    fontWeight: 600,
                    color: "#0c4a6e",
                    lineHeight: 1.5,
                  }}
                >
                  {data.title_analysis.our_title}
                </div>
                {data.title_analysis.avg_comp_title_len && (
                  <div style={{ fontSize: 11, color: "#0369a1", marginTop: 8 }}>
                    Competitor avg: {data.title_analysis.avg_comp_title_len}{" "}
                    chars
                    {data.title_analysis.our_title_length <
                    data.title_analysis.avg_comp_title_len
                      ? " — your title is shorter than most competitors"
                      : " — length is fine"}
                  </div>
                )}
              </div>

              {/* Missing keywords */}
              {data.title_analysis.missing_keywords?.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <div
                    style={{
                      fontSize: 11,
                      fontWeight: 800,
                      color: "#991b1b",
                      textTransform: "uppercase",
                      letterSpacing: 0.5,
                      marginBottom: 8,
                    }}
                  >
                    ❌ Keywords your title is MISSING (used by competitors)
                  </div>
                  <div>
                    {data.title_analysis.missing_keywords.map((k) => (
                      <KeywordChip
                        key={k.word}
                        word={k.word}
                        count={k.count}
                        pctVal={k.pct}
                        missing={true}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Unique words */}
              {data.title_analysis.unique_words?.length > 0 && (
                <div>
                  <div
                    style={{
                      fontSize: 11,
                      fontWeight: 800,
                      color: "#065f46",
                      textTransform: "uppercase",
                      letterSpacing: 0.5,
                      marginBottom: 8,
                    }}
                  >
                    ✓ Words only YOU use (unique differentiators)
                  </div>
                  <div>
                    {data.title_analysis.unique_words.map((w) => (
                      <KeywordChip
                        key={w}
                        word={w}
                        pctVal={0}
                        missing={false}
                      />
                    ))}
                  </div>
                </div>
              )}
            </Section>

            {/* ── Condition analysis ── */}
            <Section title="Condition Analysis" icon="🏷">
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 16,
                  marginBottom: 16,
                }}
              >
                <div
                  style={{
                    background: "#f0f9ff",
                    borderRadius: 8,
                    padding: 14,
                  }}
                >
                  <div
                    style={{
                      fontSize: 10,
                      color: "#0369a1",
                      fontWeight: 700,
                      textTransform: "uppercase",
                      marginBottom: 4,
                    }}
                  >
                    Your Condition
                  </div>
                  <div
                    style={{ fontSize: 15, fontWeight: 800, color: "#0c4a6e" }}
                  >
                    {data.condition_analysis.our_condition || "Not set"}
                  </div>
                </div>
                <div
                  style={{
                    background: "#f9fafb",
                    borderRadius: 8,
                    padding: 14,
                  }}
                >
                  <div
                    style={{
                      fontSize: 10,
                      color: "#6b7280",
                      fontWeight: 700,
                      textTransform: "uppercase",
                      marginBottom: 4,
                    }}
                  >
                    Most common (competitors)
                  </div>
                  <div
                    style={{ fontSize: 15, fontWeight: 800, color: "#374151" }}
                  >
                    {data.condition_analysis.dominant_condition || "—"}
                  </div>
                </div>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {Object.entries(data.condition_analysis.condition_counts || {})
                  .sort((a, b) => b[1] - a[1])
                  .map(([cond, count]) => {
                    const total = Object.values(
                      data.condition_analysis.condition_counts,
                    ).reduce((a, b) => a + b, 0);
                    return (
                      <div
                        key={cond}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 10,
                        }}
                      >
                        <div
                          style={{
                            width: 160,
                            fontSize: 11,
                            color: "#374151",
                            fontWeight: 500,
                            flexShrink: 0,
                          }}
                        >
                          {cond}
                        </div>
                        <div
                          style={{
                            flex: 1,
                            background: "#f3f4f6",
                            borderRadius: 99,
                            height: 8,
                            overflow: "hidden",
                          }}
                        >
                          <div
                            style={{
                              height: "100%",
                              borderRadius: 99,
                              background: "#6d28d9",
                              width: `${(count / total) * 100}%`,
                            }}
                          />
                        </div>
                        <div
                          style={{
                            fontSize: 11,
                            fontWeight: 700,
                            color: "#374151",
                            width: 40,
                            textAlign: "right",
                          }}
                        >
                          {count} ({Math.round((count / total) * 100)}%)
                        </div>
                      </div>
                    );
                  })}
              </div>
            </Section>

            {/* ── Competitors ── */}
            <Section
              title={`Top Competitors (${data.competitors.total})`}
              icon="👥"
            >
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {data.competitors.data.map((c, i) => {
                  const priceDiff =
                    data.price_analysis.our_price && c.price_amount
                      ? (
                          data.price_analysis.our_price - c.price_amount
                        ).toFixed(2)
                      : null;
                  return (
                    <a
                      key={c.id}
                      href={c.external_url}
                      target="_blank"
                      rel="noreferrer"
                      style={{
                        textDecoration: "none",
                        display: "flex",
                        gap: 14,
                        alignItems: "flex-start",
                        background: "#f9fafb",
                        border: "1px solid #e5e7eb",
                        borderRadius: 10,
                        padding: "12px 14px",
                        transition: "all 0.12s",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = "#1d4ed8";
                        e.currentTarget.style.background = "#eff6ff";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = "#e5e7eb";
                        e.currentTarget.style.background = "#f9fafb";
                      }}
                    >
                      <div
                        style={{
                          width: 24,
                          height: 24,
                          borderRadius: "50%",
                          flexShrink: 0,
                          background: i < 3 ? "#1d4ed8" : "#e5e7eb",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: 10,
                          fontWeight: 800,
                          color: i < 3 ? "#fff" : "#6b7280",
                        }}
                      >
                        {i + 1}
                      </div>

                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div
                          style={{
                            fontSize: 12,
                            fontWeight: 700,
                            color: "#1d4ed8",
                            marginBottom: 6,
                            lineHeight: 1.4,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {c.title || "—"}
                        </div>
                        <div
                          style={{
                            display: "flex",
                            gap: 12,
                            flexWrap: "wrap",
                            alignItems: "center",
                          }}
                        >
                          <span
                            style={{
                              fontSize: 15,
                              fontWeight: 800,
                              color: "#111827",
                            }}
                          >
                            {fmtPrice(c.price_amount, c.price_currency)}
                          </span>
                          {priceDiff != null && (
                            <span
                              style={{
                                fontSize: 10,
                                fontWeight: 700,
                                padding: "2px 7px",
                                borderRadius: 20,
                                background:
                                  priceDiff > 0
                                    ? "#fee2e2"
                                    : priceDiff < 0
                                      ? "#d1fae5"
                                      : "#f3f4f6",
                                color:
                                  priceDiff > 0
                                    ? "#991b1b"
                                    : priceDiff < 0
                                      ? "#065f46"
                                      : "#374151",
                              }}
                            >
                              {priceDiff > 0
                                ? `You're £${priceDiff} higher`
                                : priceDiff < 0
                                  ? `You're £${Math.abs(priceDiff)} lower`
                                  : "Same price"}
                            </span>
                          )}
                          {c.condition && (
                            <span
                              style={{
                                fontSize: 10,
                                background: "#e5e7eb",
                                borderRadius: 4,
                                padding: "2px 6px",
                                color: "#374151",
                                fontWeight: 600,
                              }}
                            >
                              {c.condition}
                            </span>
                          )}
                          {c.watcher_count > 0 && (
                            <span style={{ fontSize: 10, color: "#6b7280" }}>
                              👁 {c.watcher_count}
                            </span>
                          )}
                          {c.seller_id && (
                            <span style={{ fontSize: 10, color: "#9ca3af" }}>
                              @{c.seller_id}
                            </span>
                          )}
                        </div>
                      </div>
                      <span style={{ fontSize: 14, color: "#9ca3af" }}>↗</span>
                    </a>
                  );
                })}
              </div>
            </Section>
          </>
        )}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// COMPARE DRAWER — summary version
// ─────────────────────────────────────────────────────────────────────────────
const CompareDrawer = ({ ebayItemId, onClose, onViewFull }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    setData(null);
    axios
      .get(`${API_BASE}/analysis/${ebayItemId}`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e.response?.data?.message || e.message))
      .finally(() => setLoading(false));
  }, [ebayItemId]);

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        right: 0,
        bottom: 0,
        width: "min(480px,100vw)",
        background: "#fff",
        boxShadow: "-8px 0 40px rgba(0,0,0,0.14)",
        zIndex: 1000,
        display: "flex",
        flexDirection: "column",
        animation: "slideIn 0.22s ease",
      }}
    >
      <style>{`@keyframes slideIn{from{transform:translateX(20px);opacity:0}to{transform:translateX(0);opacity:1}}`}</style>

      {/* Header */}
      <div
        style={{
          padding: "16px 20px",
          borderBottom: "1px solid #e5e7eb",
          background: "#fafafa",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          flexShrink: 0,
        }}
      >
        <div>
          <div style={{ fontSize: 14, fontWeight: 800, color: "#111827" }}>
            Quick Analysis
          </div>
          <div
            style={{
              fontSize: 10,
              color: "#9ca3af",
              fontFamily: "monospace",
              marginTop: 2,
            }}
          >
            #{ebayItemId}
          </div>
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          {data && (
            <button
              onClick={onViewFull}
              style={{
                background: "#111827",
                color: "#fff",
                border: "none",
                borderRadius: 8,
                padding: "6px 14px",
                fontSize: 12,
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              Full Analysis →
            </button>
          )}
          <button
            onClick={onClose}
            style={{
              background: "#f3f4f6",
              border: "none",
              borderRadius: 8,
              width: 32,
              height: 32,
              cursor: "pointer",
              fontSize: 16,
              color: "#6b7280",
            }}
          >
            ✕
          </button>
        </div>
      </div>

      {/* Body */}
      <div style={{ flex: 1, overflowY: "auto", padding: "18px 20px" }}>
        {loading && (
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <Sk w="70%" h={16} />
            <Sk w="100%" h={80} r={10} />
            <Sk w="50%" h={14} />
            {[...Array(4)].map((_, i) => (
              <Sk key={i} w="100%" h={60} r={8} />
            ))}
          </div>
        )}

        {error && (
          <div
            style={{
              background: "#fee2e2",
              borderRadius: 10,
              padding: 16,
              color: "#991b1b",
              fontSize: 13,
            }}
          >
            ⚠️ {error}
          </div>
        )}

        {data && !loading && (
          <>
            {/* Our listing quick stats */}
            <div
              style={{
                background: "#f0f9ff",
                border: "1px solid #bae6fd",
                borderRadius: 10,
                padding: 14,
                marginBottom: 16,
              }}
            >
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: "#0c4a6e",
                  marginBottom: 8,
                  lineHeight: 1.4,
                }}
              >
                {data.our_listing?.title}
              </div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(4,1fr)",
                  gap: 6,
                }}
              >
                {[
                  ["Price", fmtPrice(data.our_listing?.price)],
                  ["Impressions", fmt(data.our_listing?.impressions)],
                  ["CTR", pct(data.our_listing?.ctr)],
                  ["Sold", fmt(data.our_listing?.sold)],
                ].map(([k, v]) => (
                  <div
                    key={k}
                    style={{
                      background: "#fff",
                      borderRadius: 6,
                      padding: "6px 8px",
                      textAlign: "center",
                    }}
                  >
                    <div
                      style={{
                        fontSize: 9,
                        color: "#6b7280",
                        fontWeight: 700,
                        textTransform: "uppercase",
                      }}
                    >
                      {k}
                    </div>
                    <div
                      style={{
                        fontSize: 13,
                        fontWeight: 800,
                        color: "#0c4a6e",
                        marginTop: 2,
                      }}
                    >
                      {v}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Top action items */}
            {data.actions?.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <div
                  style={{
                    fontSize: 10,
                    fontWeight: 800,
                    color: "#9ca3af",
                    textTransform: "uppercase",
                    letterSpacing: 1,
                    marginBottom: 10,
                  }}
                >
                  ⚡ Issues to fix ({data.actions.length})
                </div>
                <div
                  style={{ display: "flex", flexDirection: "column", gap: 8 }}
                >
                  {data.actions.slice(0, 3).map((a, i) => {
                    const cfg = PRIORITY_CFG[a.priority] || PRIORITY_CFG.low;
                    return (
                      <div
                        key={i}
                        style={{
                          background: cfg.bg,
                          border: `1px solid ${cfg.border}`,
                          borderLeft: `3px solid ${cfg.color}`,
                          borderRadius: 8,
                          padding: "10px 12px",
                        }}
                      >
                        <div
                          style={{
                            fontSize: 11,
                            fontWeight: 700,
                            color: cfg.color,
                            marginBottom: 4,
                          }}
                        >
                          {AREA_ICON[a.area]} {a.issue}
                        </div>
                        <div style={{ fontSize: 11, color: "#374151" }}>
                          ✏️ {a.fix}
                        </div>
                      </div>
                    );
                  })}
                  {data.actions.length > 3 && (
                    <button
                      onClick={onViewFull}
                      style={{
                        background: "none",
                        border: "1px dashed #d1d5db",
                        borderRadius: 8,
                        padding: "8px",
                        fontSize: 12,
                        color: "#6b7280",
                        cursor: "pointer",
                        fontWeight: 600,
                      }}
                    >
                      +{data.actions.length - 3} more issues → View full
                      analysis
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Price summary */}
            <div style={{ marginBottom: 16 }}>
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 800,
                  color: "#9ca3af",
                  textTransform: "uppercase",
                  letterSpacing: 1,
                  marginBottom: 10,
                }}
              >
                💰 Price vs Competitors
              </div>
              <div
                style={{
                  background: "#fff",
                  border: "1px solid #e5e7eb",
                  borderRadius: 10,
                  padding: 12,
                }}
              >
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(3,1fr)",
                    gap: 8,
                    marginBottom: 10,
                  }}
                >
                  {[
                    [
                      "Ours",
                      fmtPrice(data.price_analysis.our_price),
                      "#1d4ed8",
                    ],
                    ["Avg", fmtPrice(data.price_analysis.avg_price), "#374151"],
                    [
                      "Lowest",
                      fmtPrice(data.price_analysis.min_price),
                      "#065f46",
                    ],
                  ].map(([k, v, col]) => (
                    <div key={k} style={{ textAlign: "center" }}>
                      <div
                        style={{
                          fontSize: 9,
                          color: "#9ca3af",
                          fontWeight: 700,
                          textTransform: "uppercase",
                        }}
                      >
                        {k}
                      </div>
                      <div
                        style={{ fontSize: 14, fontWeight: 800, color: col }}
                      >
                        {v}
                      </div>
                    </div>
                  ))}
                </div>
                <PriceBandChart
                  bands={data.price_analysis.price_bands}
                  ourPrice={data.price_analysis.our_price}
                />
              </div>
            </div>

            {/* Missing keywords */}
            {data.title_analysis.missing_keywords?.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <div
                  style={{
                    fontSize: 10,
                    fontWeight: 800,
                    color: "#9ca3af",
                    textTransform: "uppercase",
                    letterSpacing: 1,
                    marginBottom: 8,
                  }}
                >
                  📝 Missing from your title
                </div>
                <div>
                  {data.title_analysis.missing_keywords.slice(0, 5).map((k) => (
                    <KeywordChip
                      key={k.word}
                      word={k.word}
                      count={k.count}
                      pctVal={k.pct}
                      missing={true}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Top 5 competitors */}
            <div>
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 800,
                  color: "#9ca3af",
                  textTransform: "uppercase",
                  letterSpacing: 1,
                  marginBottom: 8,
                }}
              >
                👥 Top Competitors ({data.competitors.total})
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {data.competitors.data.slice(0, 5).map((c, i) => {
                  const diff =
                    data.price_analysis.our_price && c.price_amount
                      ? (
                          data.price_analysis.our_price - c.price_amount
                        ).toFixed(2)
                      : null;
                  return (
                    <a
                      key={c.id}
                      href={c.external_url}
                      target="_blank"
                      rel="noreferrer"
                      style={{
                        textDecoration: "none",
                        background: "#f9fafb",
                        border: "1px solid #e5e7eb",
                        borderRadius: 8,
                        padding: "10px 12px",
                        display: "flex",
                        gap: 10,
                        alignItems: "center",
                        transition: "all 0.12s",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = "#1d4ed8";
                        e.currentTarget.style.background = "#eff6ff";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = "#e5e7eb";
                        e.currentTarget.style.background = "#f9fafb";
                      }}
                    >
                      <div
                        style={{
                          width: 20,
                          height: 20,
                          borderRadius: "50%",
                          background: i < 3 ? "#1d4ed8" : "#e5e7eb",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: 9,
                          fontWeight: 800,
                          color: i < 3 ? "#fff" : "#6b7280",
                          flexShrink: 0,
                        }}
                      >
                        {i + 1}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div
                          style={{
                            fontSize: 11,
                            fontWeight: 600,
                            color: "#1d4ed8",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {c.title || "—"}
                        </div>
                        <div
                          style={{
                            display: "flex",
                            gap: 8,
                            marginTop: 3,
                            alignItems: "center",
                          }}
                        >
                          <span
                            style={{
                              fontSize: 13,
                              fontWeight: 800,
                              color: "#111827",
                            }}
                          >
                            {fmtPrice(c.price_amount)}
                          </span>
                          {diff != null && (
                            <span
                              style={{
                                fontSize: 10,
                                fontWeight: 700,
                                color: diff > 0 ? "#991b1b" : "#065f46",
                              }}
                            >
                              {diff > 0
                                ? `↑ £${diff} more`
                                : `↓ £${Math.abs(diff)} less`}
                            </span>
                          )}
                          {c.condition && (
                            <span
                              style={{
                                fontSize: 9,
                                color: "#6b7280",
                                background: "#e5e7eb",
                                borderRadius: 4,
                                padding: "1px 5px",
                              }}
                            >
                              {c.condition}
                            </span>
                          )}
                        </div>
                      </div>
                      <span style={{ fontSize: 12, color: "#9ca3af" }}>↗</span>
                    </a>
                  );
                })}
                {data.competitors.total > 5 && (
                  <button
                    onClick={onViewFull}
                    style={{
                      background: "none",
                      border: "1px dashed #d1d5db",
                      borderRadius: 8,
                      padding: 8,
                      fontSize: 11,
                      color: "#6b7280",
                      cursor: "pointer",
                      fontWeight: 600,
                    }}
                  >
                    +{data.competitors.total - 5} more competitors → Full
                    analysis
                  </button>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// MAIN PAGE
// ─────────────────────────────────────────────────────────────────────────────
const ListingAgentPage = () => {
  const [view, setView] = useState("list"); // list | detail
  const [detailId, setDetailId] = useState(null);
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [findingCompetitors, setFindingCompetitors] = useState(false);
  const [findingOne, setFindingOne] = useState(null);
  const [drawerItem, setDrawerItem] = useState(null);
  const [toast, setToast] = useState(null);
  const [companyId, setCompanyId] = useState(null);

  useEffect(() => {
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      setCompanyId(user.company_id || user.companyId || null);
    } catch {
      setCompanyId(null);
    }
  }, []);

  const showToast = (msg, type = "ok") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  const fetchStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const r = await axios.get(`${API_BASE}/stats`);
      if (r.data.success) setStats(r.data);
    } catch (e) {
      console.error(e.message);
    } finally {
      setStatsLoading(false);
    }
  }, []);

  const fetchListings = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 100 };
      if (companyId) params.company_id = companyId;
      const r = await axios.get(`${API_BASE}/low-performing`, { params });
      if (r.data.success) setListings(r.data.data || []);
    } catch (e) {
      console.error(e.message);
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    fetchStats();
    fetchListings();
  }, [fetchStats, fetchListings]);

  const handleFindAll = async () => {
    setFindingCompetitors(true);
    try {
      const body = {};
      if (companyId) body.company_id = companyId;
      const r = await axios.post(`${API_BASE}/find-competitors`, body);
      showToast(`✓ Processed ${r.data.processed} listings`);
      await Promise.all([fetchStats(), fetchListings()]);
    } catch (e) {
      showToast(e.response?.data?.error || e.message, "err");
    } finally {
      setFindingCompetitors(false);
    }
  };

  const handleFindOne = async (id) => {
    setFindingOne(id);
    try {
      const body = { ebay_item_ids: [id] };
      if (companyId) body.company_id = companyId;
      await axios.post(`${API_BASE}/find-competitors`, body);
      showToast("Competitors updated");
      fetchStats();
    } catch (e) {
      showToast(e.response?.data?.error || e.message, "err");
    } finally {
      setFindingOne(null);
    }
  };

  // Show detail page
  if (view === "detail" && detailId) {
    return (
      <ListingDetailPage
        ebayItemId={detailId}
        onBack={() => {
          setView("list");
          setDetailId(null);
        }}
      />
    );
  }

  const totalRecs = stats
    ? (stats.recommendations || []).reduce((s, r) => s + Number(r.count), 0)
    : 0;
  const pendingRecs = stats
    ? ((stats.recommendations || []).find((r) => r.status === "pending")
        ?.count ?? 0)
    : 0;

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#f9fafb",
        fontFamily: "-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif",
      }}
    >
      <style>{`
        @keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
        @keyframes fadeIn  { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }
        @keyframes spin    { to{transform:rotate(360deg)} }
        @keyframes slideIn { from{transform:translateX(20px);opacity:0} to{transform:translateX(0);opacity:1} }
        @keyframes toastIn { from{opacity:0;transform:translateY(-8px)} to{opacity:1;transform:translateY(0)} }
      `}</style>

      {/* Toast */}
      {toast && (
        <div
          style={{
            position: "fixed",
            top: 20,
            right: 20,
            zIndex: 9999,
            background: toast.type === "ok" ? "#ecfdf5" : "#fee2e2",
            border: `1px solid ${toast.type === "ok" ? "#6ee7b7" : "#fca5a5"}`,
            borderRadius: 12,
            padding: "12px 18px",
            color: toast.type === "ok" ? "#065f46" : "#991b1b",
            fontWeight: 600,
            fontSize: 13,
            boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
            display: "flex",
            alignItems: "center",
            gap: 8,
            animation: "toastIn 0.2s ease",
          }}
        >
          {toast.type === "ok" ? "✓" : "✕"} {toast.msg}
        </div>
      )}

      {/* Drawer */}
      {drawerItem && (
        <>
          <div
            onClick={() => setDrawerItem(null)}
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0,0,0,0.35)",
              zIndex: 999,
              backdropFilter: "blur(3px)",
            }}
          />
          <CompareDrawer
            ebayItemId={drawerItem}
            onClose={() => setDrawerItem(null)}
            onViewFull={() => {
              setDrawerItem(null);
              setDetailId(drawerItem);
              setView("detail");
            }}
          />
        </>
      )}

      <div style={{ maxWidth: 1280, margin: "0 auto", padding: "32px 24px" }}>
        {/* Header */}
        <div
          style={{
            marginBottom: 28,
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 16,
          }}
        >
          <div>
            <div
              style={{
                fontSize: 10,
                fontWeight: 700,
                color: "#6d28d9",
                textTransform: "uppercase",
                letterSpacing: 1.5,
                marginBottom: 6,
              }}
            >
              Listing Intelligence
            </div>
            <h1
              style={{
                fontSize: 28,
                fontWeight: 800,
                color: "#111827",
                margin: 0,
                letterSpacing: -0.5,
              }}
            >
              Listing Agent
            </h1>
            <p style={{ fontSize: 13, color: "#6b7280", margin: "6px 0 0" }}>
              Spots low performers · finds who's beating you · tells you exactly
              what to fix
            </p>
          </div>
          <button
            onClick={handleFindAll}
            disabled={findingCompetitors || loading}
            style={{
              background: findingCompetitors ? "#e5e7eb" : "#111827",
              color: findingCompetitors ? "#6b7280" : "#fff",
              border: "none",
              borderRadius: 10,
              padding: "12px 24px",
              fontSize: 14,
              fontWeight: 700,
              cursor: findingCompetitors ? "not-allowed" : "pointer",
              display: "flex",
              alignItems: "center",
              gap: 8,
              boxShadow: findingCompetitors
                ? "none"
                : "0 2px 8px rgba(0,0,0,0.15)",
            }}
          >
            {findingCompetitors ? (
              <>
                <span
                  style={{
                    width: 14,
                    height: 14,
                    border: "2px solid #9ca3af",
                    borderTopColor: "transparent",
                    borderRadius: "50%",
                    display: "inline-block",
                    animation: "spin 0.7s linear infinite",
                  }}
                />{" "}
                Finding…
              </>
            ) : (
              "🔍 Find All Competitors"
            )}
          </button>
        </div>

        {/* Progress */}
        {findingCompetitors && (
          <div
            style={{
              background: "#fff",
              border: "1px solid #e5e7eb",
              borderRadius: 12,
              padding: "16px 20px",
              marginBottom: 20,
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: 8,
              }}
            >
              <span style={{ fontSize: 13, fontWeight: 600, color: "#111827" }}>
                Searching eBay for competitors…
              </span>
            </div>
            <div
              style={{
                background: "#f3f4f6",
                borderRadius: 99,
                height: 6,
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  height: "100%",
                  borderRadius: 99,
                  background: "linear-gradient(90deg,#1d4ed8,#6d28d9)",
                  width: "60%",
                  animation: "pulse 1.5s ease infinite",
                }}
              />
            </div>
          </div>
        )}

        {/* Stat cards */}
        <div
          style={{
            display: "flex",
            gap: 14,
            flexWrap: "wrap",
            marginBottom: 28,
          }}
        >
          {[
            {
              icon: "📉",
              label: "Low Performing",
              val: statsLoading
                ? "—"
                : fmt(stats?.low_performing_listings ?? 0),
              sub: "impressions < 20 or CTR < 2%",
              accent: "#dc2626",
            },
            {
              icon: "👥",
              label: "Competitors Tracked",
              val: statsLoading ? "—" : fmt(stats?.competitors?.total ?? 0),
              sub: `${fmt(stats?.competitors?.sold ?? 0)} sold/ended`,
              accent: "#1d4ed8",
            },
            {
              icon: "💡",
              label: "Recommendations",
              val: statsLoading ? "—" : fmt(totalRecs),
              sub: `${fmt(pendingRecs)} pending review`,
              accent: "#6d28d9",
            },
          ].map((s) => (
            <div
              key={s.label}
              style={{
                flex: 1,
                minWidth: 160,
                background: "#fff",
                border: "1px solid #e5e7eb",
                borderRadius: 14,
                padding: "20px 24px",
                borderTop: `3px solid ${s.accent}`,
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  marginBottom: 10,
                }}
              >
                <span style={{ fontSize: 16 }}>{s.icon}</span>
                <span
                  style={{
                    fontSize: 10,
                    fontWeight: 800,
                    color: "#9ca3af",
                    textTransform: "uppercase",
                    letterSpacing: 0.8,
                  }}
                >
                  {s.label}
                </span>
              </div>
              {statsLoading ? (
                <Sk w={60} h={30} r={6} style={{ marginBottom: 8 }} />
              ) : (
                <div
                  style={{
                    fontSize: 30,
                    fontWeight: 800,
                    color: s.accent,
                    letterSpacing: -1,
                  }}
                >
                  {s.val}
                </div>
              )}
              <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 4 }}>
                {s.sub}
              </div>
            </div>
          ))}
        </div>

        {/* Table */}
        <div
          style={{
            background: "#fff",
            border: "1px solid #e5e7eb",
            borderRadius: 16,
            overflow: "hidden",
            boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
          }}
        >
          <div
            style={{
              padding: "16px 20px",
              borderBottom: "1px solid #e5e7eb",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ fontSize: 15, fontWeight: 800, color: "#111827" }}>
                Low Performing Listings
              </span>
              {!loading && (
                <span
                  style={{
                    background: "#fef3c7",
                    color: "#92400e",
                    border: "1px solid #fde68a",
                    borderRadius: 20,
                    padding: "2px 10px",
                    fontSize: 11,
                    fontWeight: 700,
                  }}
                >
                  {listings.length}
                </span>
              )}
            </div>
            <button
              onClick={fetchListings}
              disabled={loading}
              style={{
                background: "none",
                border: "1px solid #e5e7eb",
                borderRadius: 8,
                padding: "6px 14px",
                fontSize: 12,
                fontWeight: 600,
                color: "#374151",
                cursor: "pointer",
              }}
            >
              {loading ? "Loading…" : "↺ Refresh"}
            </button>
          </div>

          {!loading && listings.length === 0 && (
            <div style={{ padding: "60px 24px", textAlign: "center" }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>🎉</div>
              <div
                style={{
                  fontSize: 16,
                  fontWeight: 700,
                  color: "#111827",
                  marginBottom: 6,
                }}
              >
                All listings are performing well
              </div>
              <div style={{ fontSize: 13, color: "#9ca3af" }}>
                No listings below the threshold
              </div>
            </div>
          )}

          {(loading || listings.length > 0) && (
            <div style={{ overflowX: "auto" }}>
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: 13,
                }}
              >
                <thead>
                  <tr
                    style={{
                      background: "#f9fafb",
                      borderBottom: "1px solid #e5e7eb",
                    }}
                  >
                    {[
                      "Title",
                      "Impressions",
                      "Views",
                      "CTR",
                      "Sold",
                      "Price",
                      "Issues",
                      "Actions",
                    ].map((h) => (
                      <th
                        key={h}
                        style={{
                          padding: "10px 16px",
                          textAlign: "left",
                          fontSize: 10,
                          fontWeight: 800,
                          color: "#6b7280",
                          textTransform: "uppercase",
                          letterSpacing: 0.8,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {loading
                    ? [...Array(8)].map((_, i) => <SkRow key={i} />)
                    : listings.map((item, i) => {
                        const flags = [];
                        if ((item.impressions ?? 0) < 20)
                          flags.push("low_impressions");
                        if ((item.ctr ?? item.click_through_rate ?? 0) < 2)
                          flags.push("low_ctr");
                        if ((item.ebay_views ?? 0) === 0)
                          flags.push("no_views");
                        if ((item.quantity_sold ?? 0) === 0)
                          flags.push("no_sales");
                        const isFindingThis = findingOne === item.ebay_item_id;

                        const FLAG_CFG2 = {
                          low_impressions: {
                            label: "Low Impressions",
                            color: "#92400e",
                            bg: "#fef3c7",
                            border: "#fde68a",
                          },
                          low_ctr: {
                            label: "Low CTR",
                            color: "#6d28d9",
                            bg: "#ede9fe",
                            border: "#c4b5fd",
                          },
                          no_views: {
                            label: "No Views",
                            color: "#991b1b",
                            bg: "#fee2e2",
                            border: "#fca5a5",
                          },
                          no_sales: {
                            label: "No Sales",
                            color: "#1e40af",
                            bg: "#dbeafe",
                            border: "#93c5fd",
                          },
                        };

                        return (
                          <tr
                            key={item.ebay_item_id || i}
                            style={{
                              borderBottom: "1px solid #f3f4f6",
                              transition: "background 0.1s",
                              animation: `fadeIn 0.25s ease ${i * 0.02}s both`,
                            }}
                            onMouseEnter={(e) =>
                              (e.currentTarget.style.background = "#fafafa")
                            }
                            onMouseLeave={(e) =>
                              (e.currentTarget.style.background = "")
                            }
                          >
                            <td style={{ padding: "13px 16px", maxWidth: 300 }}>
                              <a
                                href={item.item_url}
                                target="_blank"
                                rel="noreferrer"
                                style={{
                                  color: "#1d4ed8",
                                  textDecoration: "none",
                                  fontWeight: 600,
                                  lineHeight: 1.4,
                                  display: "block",
                                  marginBottom: 3,
                                }}
                                title={item.title}
                              >
                                {item.title?.length > 55
                                  ? item.title.slice(0, 55) + "…"
                                  : item.title || "—"}
                              </a>
                              <span
                                style={{
                                  fontSize: 10,
                                  color: "#9ca3af",
                                  fontFamily: "monospace",
                                }}
                              >
                                #{item.ebay_item_id}
                              </span>
                            </td>
                            <td style={{ padding: "13px 16px" }}>
                              <span
                                style={{
                                  fontWeight: 800,
                                  fontSize: 16,
                                  color:
                                    (item.impressions ?? 0) < 20
                                      ? "#dc2626"
                                      : "#111827",
                                }}
                              >
                                {fmt(item.impressions)}
                              </span>
                              {(item.impressions ?? 0) < 5 && (
                                <span style={{ marginLeft: 4, fontSize: 11 }}>
                                  🔴
                                </span>
                              )}
                            </td>
                            <td style={{ padding: "13px 16px" }}>
                              <span
                                style={{
                                  fontWeight: 700,
                                  color:
                                    (item.ebay_views ?? 0) === 0
                                      ? "#dc2626"
                                      : "#111827",
                                }}
                              >
                                {fmt(item.ebay_views)}
                              </span>
                            </td>
                            <td
                              style={{
                                padding: "13px 16px",
                                whiteSpace: "nowrap",
                              }}
                            >
                              <span
                                style={{
                                  fontWeight: 700,
                                  color:
                                    (item.ctr ?? item.click_through_rate ?? 0) <
                                    2
                                      ? "#6d28d9"
                                      : "#111827",
                                }}
                              >
                                {item.ctr ?? item.click_through_rate ?? "—"}
                                {(item.ctr ?? item.click_through_rate) != null
                                  ? "%"
                                  : ""}
                              </span>
                            </td>
                            <td style={{ padding: "13px 16px" }}>
                              <span
                                style={{ fontWeight: 700, color: "#374151" }}
                              >
                                {fmt(item.quantity_sold)}
                              </span>
                            </td>
                            <td
                              style={{
                                padding: "13px 16px",
                                whiteSpace: "nowrap",
                              }}
                            >
                              <span
                                style={{ fontWeight: 700, color: "#111827" }}
                              >
                                {fmtPrice(item.price, item.currency)}
                              </span>
                            </td>
                            <td style={{ padding: "13px 16px", minWidth: 140 }}>
                              <div
                                style={{ display: "flex", flexWrap: "wrap" }}
                              >
                                {flags.map((f) => {
                                  const c = FLAG_CFG2[f] || {
                                    label: f,
                                    color: "#374151",
                                    bg: "#f3f4f6",
                                    border: "#d1d5db",
                                  };
                                  return (
                                    <span
                                      key={f}
                                      style={{
                                        display: "inline-block",
                                        padding: "2px 8px",
                                        borderRadius: 20,
                                        fontSize: 9,
                                        fontWeight: 700,
                                        color: c.color,
                                        background: c.bg,
                                        border: `1px solid ${c.border}`,
                                        marginRight: 3,
                                        marginBottom: 3,
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      {c.label}
                                    </span>
                                  );
                                })}
                              </div>
                            </td>
                            <td
                              style={{
                                padding: "13px 16px",
                                whiteSpace: "nowrap",
                              }}
                            >
                              <div style={{ display: "flex", gap: 6 }}>
                                <button
                                  onClick={() =>
                                    setDrawerItem(item.ebay_item_id)
                                  }
                                  style={{
                                    background: "#eff6ff",
                                    color: "#1d4ed8",
                                    border: "1px solid #bfdbfe",
                                    borderRadius: 7,
                                    padding: "6px 12px",
                                    fontSize: 12,
                                    fontWeight: 700,
                                    cursor: "pointer",
                                  }}
                                >
                                  Quick View
                                </button>
                                <button
                                  onClick={() => {
                                    setDetailId(item.ebay_item_id);
                                    setView("detail");
                                  }}
                                  style={{
                                    background: "#f0fdf4",
                                    color: "#065f46",
                                    border: "1px solid #86efac",
                                    borderRadius: 7,
                                    padding: "6px 12px",
                                    fontSize: 12,
                                    fontWeight: 700,
                                    cursor: "pointer",
                                  }}
                                >
                                  Full Analysis
                                </button>
                                <button
                                  onClick={() =>
                                    handleFindOne(item.ebay_item_id)
                                  }
                                  disabled={isFindingThis}
                                  style={{
                                    background: "#f9fafb",
                                    color: "#374151",
                                    border: "1px solid #e5e7eb",
                                    borderRadius: 7,
                                    padding: "6px 10px",
                                    fontSize: 12,
                                    cursor: isFindingThis
                                      ? "not-allowed"
                                      : "pointer",
                                    width: 34,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                  }}
                                >
                                  {isFindingThis ? (
                                    <span
                                      style={{
                                        width: 12,
                                        height: 12,
                                        border: "1.5px solid #6b7280",
                                        borderTopColor: "transparent",
                                        borderRadius: "50%",
                                        display: "inline-block",
                                        animation: "spin 0.7s linear infinite",
                                      }}
                                    />
                                  ) : (
                                    "🔍"
                                  )}
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div
          style={{
            marginTop: 20,
            fontSize: 11,
            color: "#d1d5db",
            textAlign: "center",
          }}
        >
          eBay Seller Hub data · Competitors via eBay Browse API · No customer
          tokens used
        </div>
      </div>
    </div>
  );
};

export default ListingAgentPage;
